import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { STALE_TIME } from '~/domains/contest/constants/query';
import { isLoadingFixedForDisabledQuery } from '~/domains/common/utils/isLoadingFixedForEnabled';
import { getPopularProps } from '~/domains/contest/domains/services/props.service.api';
import { parsePropToComponentProp } from '~/domains/contest/domains/quickPick/utils/parsePropToComponentProp';
import { useQuickPicksSettings } from '~/domains/contest/domains/quickPick/hooks/useQuickPicksSettings';
import type { PropPickProps } from '~/domains/contest/domains/common/types/propPickProps.type';

const HOME_QUICK_PICKS_COUNT = 8;

/**
 * Home QuickPicks promo data source.
 *
 * Unlike the board discover feed (`useQuickPicksDiscover`), the home promo sources
 * its cards from the `/v1/popular` endpoint and shows only the first
 * {@link HOME_QUICK_PICKS_COUNT} props. Shaped to match what `PopularPicksSection`
 * expects, so `HomeQuickpicks` can enrich each item with its own handlers.
 */
export function useHomeQuickPicks() {
  const settingsQuery = useQuickPicksSettings();

  const disallowed = settingsQuery.data?.state_config?.disallowed ?? [];

  const query = useQuery({
    queryKey: ['home-quick-picks-popular', disallowed],
    queryFn: () => getPopularProps({ limit: HOME_QUICK_PICKS_COUNT, offset: 0, disallowed }),
    enabled: !!settingsQuery.data,
    staleTime: STALE_TIME,
  });

  const popularProps = useMemo(
    (): PropPickProps[] =>
      (query.data?.data ?? []).slice(0, HOME_QUICK_PICKS_COUNT).map(parsePropToComponentProp),
    [query.data]
  );

  return {
    popularProps,
    isLoading:
      isLoadingFixedForDisabledQuery(query.isLoading, query.fetchStatus) ||
      isLoadingFixedForDisabledQuery(settingsQuery.isLoading, settingsQuery.fetchStatus),
    error: query.error,
  };
}
